import { apiHandler } from "assets/api";
import { endpoint } from "assets/api/endpoint";
import { useState, useCallback } from "react";

export const useDigioLiveness = ({
  customerName,
  mobileNumber,
  logo,
  templateName = "faceLiveliness",
  setDigioImage,
  setCallInProgress,
  digioEnvironment = "production",
}) => {
  const getDocumentId = async (id) => {
    const result = await apiHandler({
      url: endpoint.GET_DIGIO_DOCUMENT_ID + id + "/response",
      method: "POST",
    });

    if (result?.data) {
      await getDigioImage(result?.data?.actions?.[0]?.file_id);
    } else {
      console.log("❌ NO DIGIO RESULTS RECEIVED");
    }
  };

  const getDigioImage = async (imageId) => {
    const result = await apiHandler({
      url: endpoint.GET_DIGIO_GET_MEDIA + imageId + "?base64=true",
    });
    if(result?.data){
        setDigioImage(result?.data)
    }
    console.log("📷 DIGIO IMAGE RESULT:", result);
  };

  // ----------------------------------------------------------------------
  // ⭐ CLEAN, FIXED POPUP WATCHER (NO BUGS, NO DOUBLE TRIGGERS)
  // ----------------------------------------------------------------------
  const watchDigioPopup = (onClose) => {
    if (typeof window === "undefined") return;

    let popupRef = null;
    const originalOpen = window.open;

    // Intercept popup
    window.open = function (...args) {
      popupRef = originalOpen.apply(window, args);
      return popupRef;
    };

    // Restore window.open after Digio triggers
    setTimeout(() => {
      window.open = originalOpen;
    }, 1200);

    // Check closure
    const timer = setInterval(() => {
      if (popupRef && popupRef.closed) {
        clearInterval(timer);
        onClose?.();
      }
    }, 400);
  };

  // ----------------------------------------------------------------------
  // ⭐ MAIN START FUNCTION
  // ----------------------------------------------------------------------
  const startLiveness = useCallback(async () => {
    if (typeof window === "undefined") return; // SSR guard

    setCallInProgress(true);

    try {
      // 1️⃣ Create token
      const { data } = await apiHandler({
        url: endpoint.CREATE_REQUEST_DIGIO,
        method: "POST",
        data: {
          customer_identifier: mobileNumber,
          customer_name: customerName,
          template_name: templateName,
          notify_customer: false,
          generate_access_token: true,
        },
      });

      const tokenData = data?.access_token;
      if (!tokenData) throw new Error("❌ No DigiO token received");

      if (!window.Digio) {
        console.error("❌ Digio SDK Missing");
        return;
      }

      // 2️⃣ Watch popup close
      let callbackTriggered = false;

      watchDigioPopup(() => {
        if (callbackTriggered) return;
        callbackTriggered = true;
        console.log("❌ USER CLOSED POPUP BEFORE DIGIO CALLBACK");
      });
      const digioOptions = {
        environment: digioEnvironment,
        logo: logo,
        is_iframe: true,
        callback: (response) => {
          if (callbackTriggered) return;
          callbackTriggered = true;

          if (response?.error_code) {
            console.error("❌ DIGIO CALLBACK ERROR:", response);
          } else {
            console.log("✅ DIGIO SUCCESS:", response);
            getDocumentId(response?.digio_doc_id);
          }
        },
      };

      const digio = new window.Digio(digioOptions);
      digio.init();

      digio.submit(tokenData.entity_id, mobileNumber, tokenData.id);
    } catch (error) {
      console.error("❌ LIVENESS FAILURE:", error);
    } finally {
      setCallInProgress(false);
    }
  }, []);

  return { startLiveness };
};
